import React, { useEffect, useMemo, useState } from 'react';
import { Search, Home as HomeIcon, Filter } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import FilterSidebar, { Filters } from '../components/common/FilterSidebar';
import Button from '../components/common/Button';
import PropertyCard from '../components/common/PropertyCard';
import { apiService } from '../services/api';
import { Property } from '../types';

type PurposeFilter = 'ALL' | 'SALE' | 'RENT';

const mapListingToProperty = (l: any): Property => ({
  id: l.id,
  title: l.title,
  description: l.description ?? '',
  price: l.price ?? 0,
  address: {
    street: l.address ?? '',
    city: l.city ?? '',
    state: '',
    zipCode: l.zip_code ?? l.zipCode ?? '',
    country: l.country ?? ''
  },
  images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
  type: l.type ?? l.propertyType ?? 'apartment',
  status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
  features: {
    bedrooms: l.rooms ?? 0,
    bathrooms: l.bathrooms ?? 0,
    area: Number(l.area_m2 ?? l.areaM2 ?? 0),
    garage: false,
    yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
  },
  amenities: [],
  contactInfo: { name: '', phone: '', email: '' },
  owner: l.owner_user_id ?? l.ownerUserId ?? '',
  createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
  updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
});

const parseQuery = (search: string) => {
  const params = new URLSearchParams(search);
  const purpose = params.get('purpose') ?? 'ALL';
  return {
    q: params.get('q') ?? '',
    city: params.get('city') ?? '',
    type: params.get('type') ?? '',
    zipCode: params.get('zipCode') ?? '',
    minPrice: params.get('minPrice') ?? '',
    maxPrice: params.get('maxPrice') ?? '',
    bedrooms: params.get('rooms') ?? '',
    bathrooms: params.get('bathroomsMin') ?? '',
    minArea: params.get('minArea') ?? '',
    maxArea: params.get('maxArea') ?? '',
    purpose: purpose === 'SALE' || purpose === 'RENT' ? purpose : 'ALL'
  };
};

const SearchPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const initialQuery = parseQuery(location.search);
  const [searchQuery, setSearchQuery] = useState(initialQuery.q);
  const [purpose, setPurpose] = useState<PurposeFilter>(initialQuery.purpose);
  const [filters, setFilters] = useState<Filters>({
    minPrice: initialQuery.minPrice,
    maxPrice: initialQuery.maxPrice,
    bedrooms: initialQuery.bedrooms,
    bathrooms: initialQuery.bathrooms,
    type: initialQuery.type,
    city: initialQuery.city,
    zipCode: initialQuery.zipCode,
    minArea: initialQuery.minArea,
    maxArea: initialQuery.maxArea,
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [items, setItems] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filteredItems = useMemo(() => {
    const normalize = (value?: string) => (value || '').trim().toLowerCase();
    const keyword = normalize(searchQuery);
    const matchesKeyword = (item: Property) => {
      if (!keyword) return true;
      const haystack = [
        item.title,
        item.address.street,
        item.address.city,
        item.address.state,
        item.address.zipCode,
        item.address.country
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(keyword);
    };

    return items.filter((item) => {
      if (!matchesKeyword(item)) return false;
      if (purpose === 'SALE' && item.status !== 'sale') return false;
      if (purpose === 'RENT' && item.status !== 'rent') return false;
      const addressStack = [
        item.address.street,
        item.address.city,
        item.address.state,
        item.address.zipCode,
        item.address.country
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (filters.type && item.type !== filters.type) return false;
      if (filters.city && !normalize(item.address.city).includes(normalize(filters.city))) return false;
      if (filters.zipCode && !addressStack.includes(normalize(filters.zipCode))) return false;
      if (filters.bedrooms && item.features.bedrooms < parseInt(filters.bedrooms, 10)) return false;
      if (filters.bathrooms && item.features.bathrooms < parseInt(filters.bathrooms, 10)) return false;
      if (filters.minPrice && item.price < parseInt(filters.minPrice, 10)) return false;
      if (filters.maxPrice && item.price > parseInt(filters.maxPrice, 10)) return false;
      if (filters.minArea && item.features.area < parseInt(filters.minArea, 10)) return false;
      if (filters.maxArea && item.features.area > parseInt(filters.maxArea, 10)) return false;
      return true;
    });
  }, [
    filters.bathrooms,
    filters.bedrooms,
    filters.city,
    filters.zipCode,
    filters.maxArea,
    filters.maxPrice,
    filters.minArea,
    filters.minPrice,
    filters.type,
    items,
    purpose,
    searchQuery,
  ]);

  const currentQuery = useMemo(() => parseQuery(location.search), [location.search]);

  useEffect(() => {
    setSearchQuery((prev) => (prev === currentQuery.q ? prev : currentQuery.q));
    setFilters({
      minPrice: currentQuery.minPrice,
      maxPrice: currentQuery.maxPrice,
      bedrooms: currentQuery.bedrooms,
      bathrooms: currentQuery.bathrooms,
      type: currentQuery.type,
      city: currentQuery.city,
      zipCode: currentQuery.zipCode,
      minArea: currentQuery.minArea,
      maxArea: currentQuery.maxArea,
    });
    setPurpose(currentQuery.purpose);
  }, [currentQuery]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (filters.city) params.set('city', filters.city);
    if (filters.type) params.set('type', filters.type);
    if (filters.zipCode) params.set('zipCode', filters.zipCode);
    if (filters.minPrice) params.set('minPrice', filters.minPrice);
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice);
    if (filters.bedrooms) params.set('rooms', filters.bedrooms);
    if (filters.bathrooms) params.set('bathroomsMin', filters.bathrooms);
    if (filters.minArea) params.set('minArea', filters.minArea);
    if (filters.maxArea) params.set('maxArea', filters.maxArea);
    if (purpose !== 'ALL') params.set('purpose', purpose);
    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
    if (nextSearch !== currentSearch) {
      navigate(`/search${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
    }
  }, [
    searchQuery,
    filters.city,
    filters.type,
    filters.zipCode,
    filters.minPrice,
    filters.maxPrice,
    filters.bedrooms,
    filters.bathrooms,
    filters.minArea,
    filters.maxArea,
    purpose,
    navigate,
    location.search,
  ]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const params: any = {};
        if (filters.type) params.type = filters.type;
        if (filters.minPrice) params.minPrice = parseInt(filters.minPrice, 10);
        if (filters.maxPrice) params.maxPrice = parseInt(filters.maxPrice, 10);
        if (filters.bedrooms) params.rooms = parseInt(filters.bedrooms, 10);
        if (filters.bathrooms) params.bathroomsMin = parseInt(filters.bathrooms, 10);
        if (filters.minArea) params.minArea = parseInt(filters.minArea, 10);
        if (filters.maxArea) params.maxArea = parseInt(filters.maxArea, 10);
        if (purpose !== 'ALL') params.purpose = purpose;

        const { items: resultItems } = await apiService.getPublicListings(params);
        setItems((resultItems || []).map(mapListingToProperty));
      } catch (e: any) {
        setError(e?.message || t('home.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [
    filters.type,
    filters.minPrice,
    filters.maxPrice,
    filters.bedrooms,
    filters.bathrooms,
    filters.minArea,
    filters.maxArea,
    purpose,
    t,
  ]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  const handlePropertyClick = (id: string) => {
    navigate(`/property/${id}`);
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-3">
            {t('common.searchProperties')}
          </h1>
          <p className="text-lg text-neutral-600">
            {t('home.search.premium')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6 mb-8">
          <form onSubmit={handleSearchSubmit} className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('home.searchPlaceholder')}
                  className="input-field pr-12 h-12 text-base"
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-neutral-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                  aria-label={t('common.search')}
                >
                  <Search size={18} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {([
                { value: 'ALL', label: t('common.all') },
                { value: 'SALE', label: t('common.buy') },
                { value: 'RENT', label: t('common.rent') },
              ] as { value: PurposeFilter; label: string }[]).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPurpose(option.value)}
                  className={`px-4 h-12 rounded-xl text-sm font-medium transition-all ${
                    purpose === option.value
                      ? 'bg-primary-600 text-white shadow-medium'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() => setMobileFiltersOpen(true)}
                icon={<Filter size={18} />}
                className="sm:hidden"
              >
                {t('common.filters')}
              </Button>
            </div>
          </form>
        </div>

        <div className="flex gap-8">
          <aside className="hidden sm:block w-80 flex-shrink-0">
            <div className="sticky top-24">
              <FilterSidebar filters={filters} onChange={setFilters} />
            </div>
          </aside>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-neutral-600">
                {filteredItems.length} {t('common.results')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse h-72 bg-neutral-100 rounded-2xl" />
              ))}
              {!loading && filteredItems.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  onClick={() => handlePropertyClick(property.id)}
                />
              ))}
            </div>
            {!loading && filteredItems.length === 0 && (
              <div className="text-center py-16">
                <HomeIcon size={64} className="mx-auto text-neutral-300 mb-4" />
                <h3 className="text-xl font-semibold text-neutral-700 mb-2">
                  {t('home.search.noResults')}
                </h3>
                <p className="text-neutral-500">{t('home.search.adjustFilters')}</p>
                {error && <div className="text-red-500 mt-4">{error}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 flex sm:hidden">
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} />
          <div className="relative ml-auto bg-white w-80 h-full shadow-large">
            <FilterSidebar filters={filters} onChange={setFilters} onClose={() => setMobileFiltersOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
